// hooks/useOrganizations.js
import { useOrganization } from '../contexts/OrganizationContext';

/**
 * Custom Hook: useOrganizations
 * Simplifica el acceso al OrganizationContext
 * 
 * @returns {Object} Organization context con datos y métodos
 * 
 * @example
 * const { 
 *   organizations, 
 *   currentContext, 
 *   createOrganization,
 *   changeContext 
 * } = useOrganizations();
 */
export const useOrganizations = () => {
  return useOrganization();
};

/**
 * Custom Hook: useCurrentOrganization
 * Devuelve solo la organización actual (si existe)
 * 
 * @returns {Object|null} Organización actual o null si es contexto personal
 * 
 * @example
 * const currentOrg = useCurrentOrganization();
 * if (currentOrg) {
 *   console.log(currentOrg.name, currentOrg.role);
 * }
 */
export const useCurrentOrganization = () => {
  const { currentContext, organizations } = useOrganization();
  
  if (currentContext?.type === 'ORGANIZATION') {
    return organizations.find(org => org.id === currentContext.id) || null;
  }
  
  return null;
};

/**
 * Custom Hook: useProjects
 * Devuelve proyectos y métodos relacionados
 * 
 * @returns {Object} Projects y métodos CRUD
 * 
 * @example
 * const { projects, createProject, deleteProject, loading } = useProjects();
 */
export const useProjects = () => {
  const { 
    projects, 
    projectsLoading, 
    createProject, 
    updateProject, 
    deleteProject,
    loadProjects
  } = useOrganization();
  
  return {
    projects,
    loading: projectsLoading,
    createProject,
    updateProject,
    deleteProject,
    refreshProjects: loadProjects
  };
};

/**
 * Custom Hook: useMembers
 * Devuelve métodos para gestionar miembros de organización
 * 
 * @param {string} organizationId - ID de la organización
 * @returns {Object} Métodos para gestionar miembros
 * 
 * @example
 * const { addMember, removeMember, updateRole } = useMembers(orgId);
 */
export const useMembers = (organizationId) => {
  const { addMember, removeMember, updateMemberRole } = useOrganization();
  
  return {
    addMember: (memberData) => addMember(organizationId, memberData),
    removeMember: (userId) => removeMember(organizationId, userId),
    updateRole: (userId, newRole) => updateMemberRole(organizationId, userId, newRole)
  };
};

/**
 * Custom Hook: useContextSwitcher
 * Simplifica el cambio de contexto entre Personal y Organizations
 * 
 * @returns {Object} Context actual y método para cambiarlo
 * 
 * @example
 * const { currentContext, switchToPersonal, switchToOrganization } = useContextSwitcher();
 */
export const useContextSwitcher = () => {
  const { 
    currentContext, 
    changeContext, 
    personalAccount, 
    organizations 
  } = useOrganization();
  
  const switchToPersonal = () => {
    if (personalAccount) {
      changeContext({
        type: 'PERSONAL',
        id: personalAccount.id,
        name: 'Personal'
      });
    }
  };
  
  const switchToOrganization = (organizationId) => {
    const org = organizations.find(o => o.id === organizationId);
    if (org) {
      changeContext({
        type: 'ORGANIZATION',
        id: org.id,
        name: org.name
      });
    }
  };
  
  return {
    currentContext,
    isPersonal: currentContext?.type === 'PERSONAL',
    isOrganization: currentContext?.type === 'ORGANIZATION',
    switchToPersonal,
    switchToOrganization,
    switchContext: changeContext
  };
};

export default useOrganizations;
